import type { CalendarDate } from '~/types/dates'
import { unwrapDate } from '~/types/dates'
import { daysBetween } from './calendar'
import type { Sufficiency } from './weightTrends'

/**
 * What a finished project is recorded as having covered (D-062).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  COUNTS AND COVERAGE. NEVER A SCORE.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * **No consistency score, no grade, no percentage and no streak.** The quality
 * bar requires neutral, non-punitive missed-entry language and forbids streak
 * mechanics, and a scored retrospective is a streak mechanic wearing a
 * different hat. The anti-streak rule is not limited to the Today screen, and
 * this is the screen where it would be easiest to forget that.
 *
 * The same reasoning rules out grading the project against the success criteria
 * the route engine stated at setup (D-071): a met / not-met verdict is a grade
 * in all but name. Those criteria were an aim, shown up front, and they stay
 * there.
 *
 * Pure: no clock, no storage, no formatting. Everything is counted from records
 * the caller has already gathered, so the same project always summarises the
 * same way.
 */

/** A change between two observations, or a reason there is none. */
export type SummaryChange =
  | { readonly kind: 'changed'; readonly from: number; readonly to: number; readonly delta: number }
  | { readonly kind: 'noComparison'; readonly reason: 'noStart' | 'noEnd' | 'onlyOne' }

export interface ProjectCoverage {
  /**
   * How well the project is covered by weigh-ins, in the existing D-036
   * vocabulary rather than a new one.
   *
   * Expressed against the same idea a single week uses — enough readings to
   * mean something, some, or too few — so a user reads one concept in two
   * places rather than two concepts that happen to look alike.
   */
  readonly weighIns: Sufficiency
  readonly measurements: Sufficiency
  readonly reviews: Sufficiency
}

export interface ProjectSummaryInput {
  readonly startDate: CalendarDate
  readonly endDate: CalendarDate
  /** Every weigh-in inside the project's span, in date order. */
  readonly weights: readonly { readonly date: CalendarDate; readonly weightKg: number }[]
  /** Every measurement session inside the span, in date order. */
  readonly measurements: readonly {
    readonly date: CalendarDate
    readonly waistCm: number | undefined
    readonly estimatedBodyFatPercent: number | undefined
  }[]
  /** Completed weekly reviews inside the span. */
  readonly completedReviews: number
  /** Phases the project contained, however many. */
  readonly phaseCount: number
}

export interface ProjectSummary {
  readonly durationDays: number
  readonly durationWeeks: number
  readonly phaseCount: number
  readonly weighInsRecorded: number
  readonly measurementsRecorded: number
  readonly reviewsCompleted: number
  readonly weightChangeKg: SummaryChange
  readonly waistChangeCm: SummaryChange
  /** The Navy estimate's direction of travel, never a verdict about it. */
  readonly estimateChangePercent: SummaryChange
  readonly coverage: ProjectCoverage
}

/**
 * Coverage thresholds, as fractions of what a fully covered project would hold.
 *
 * **Not coaching guidance and not a score.** They decide which of three words
 * describes the record, and they are deliberately generous: this is a
 * description of the data, and a project the user tracked most of should not be
 * described as thinly covered. Nothing downstream may turn these into a
 * percentage on a screen.
 */
export interface CoverageThresholds {
  readonly sufficientFraction: number
  readonly limitedFraction: number
}

export const DEFAULT_COVERAGE_THRESHOLDS: CoverageThresholds = {
  sufficientFraction: 0.5,
  limitedFraction: 0.2,
}

export function summariseProject(
  input: ProjectSummaryInput,
  thresholds: CoverageThresholds = DEFAULT_COVERAGE_THRESHOLDS,
): ProjectSummary {
  const durationDays = Math.max(0, daysBetween(input.startDate, input.endDate))
  const durationWeeks = Math.floor(durationDays / 7)

  const ordered = [...input.weights].sort((left, right) =>
    unwrapDate(left.date).localeCompare(unwrapDate(right.date)),
  )
  const sessions = [...input.measurements].sort((left, right) =>
    unwrapDate(left.date).localeCompare(unwrapDate(right.date)),
  )

  return {
    durationDays,
    durationWeeks,
    phaseCount: input.phaseCount,
    weighInsRecorded: ordered.length,
    measurementsRecorded: sessions.length,
    reviewsCompleted: input.completedReviews,
    weightChangeKg: changeBetween(ordered.map((entry) => entry.weightKg)),
    waistChangeCm: changeBetween(sessions.map((session) => session.waistCm)),
    estimateChangePercent: changeBetween(
      sessions.map((session) => session.estimatedBodyFatPercent),
    ),
    coverage: {
      /* One weigh-in a day, one measurement and one review a week, is what
         "fully covered" means. A project cannot exceed its own span. */
      weighIns: coverageOf(ordered.length, durationDays, thresholds),
      measurements: coverageOf(sessions.length, Math.max(1, durationWeeks), thresholds),
      reviews: coverageOf(input.completedReviews, Math.max(1, durationWeeks), thresholds),
    },
  }
}

/**
 * First to last, and nothing in between.
 *
 * **A missing value is never a zero** — a project with no measurements has no
 * waist change, not a change of zero, and the two would read identically on a
 * screen while meaning opposite things. A single observation is `onlyOne`
 * rather than a change of nothing, because one reading is a starting point and
 * not a result.
 */
function changeBetween(values: readonly (number | undefined)[]): SummaryChange {
  const present = values.filter((value): value is number => value !== undefined)

  if (present.length === 0) return { kind: 'noComparison', reason: 'noStart' }
  if (present.length === 1) return { kind: 'noComparison', reason: 'onlyOne' }

  const from = present[0] as number
  const to = present[present.length - 1] as number
  return { kind: 'changed', from, to, delta: to - from }
}

function coverageOf(
  recorded: number,
  possible: number,
  thresholds: CoverageThresholds,
): Sufficiency {
  if (possible <= 0 || recorded === 0) return 'insufficient'
  const fraction = recorded / possible
  if (fraction >= thresholds.sufficientFraction) return 'sufficient'
  if (fraction >= thresholds.limitedFraction) return 'limited'
  return 'insufficient'
}
